import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Gallery from '../components/Gallery';
import { fetchGalleries, fetchVisibleHomeBlocks } from '../services/api';
import { Gallery as GalleryType, HomeBlock } from '../types';
import { buildGalleryResponsiveSrcSet, getGalleryDisplayImageUrl } from '../utils/images';
import { buildGalleryPath } from '../utils/slug';

const Home: React.FC = () => {
    const [galleries, setGalleries] = useState<GalleryType[]>([]);
    const [homeBlocks, setHomeBlocks] = useState<HomeBlock[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadGalleries = async () => {
            try {
                const [galleriesData, blocksData] = await Promise.all([
                    fetchGalleries(),
                    fetchVisibleHomeBlocks(),
                ]);
                setGalleries(galleriesData);
                setHomeBlocks(blocksData || []);
            } catch (err) {
                setError('Kunne ikke indlæse gallerierne.');
            } finally {
                setLoading(false);
            }
        };

        loadGalleries();
    }, []);

    const allImages = useMemo(() => {
        const allImages = galleries.flatMap((gallery) =>
            (gallery.images || []).map((image) => ({
                ...image,
                galleryId: gallery.id,
                galleryTitle: gallery.title,
            }))
        );
        return allImages;
    }, [galleries]);

    const orderedVisibleBlocks = useMemo(() => {
        return [...homeBlocks]
            .filter((block) => block.isVisible !== false)
            .sort((a, b) => {
                const orderDiff = Number(a.sortOrder) - Number(b.sortOrder);
                if (orderDiff !== 0) return orderDiff;
                return Number(a.id) - Number(b.id);
            });
    }, [homeBlocks]);

    const toFeaturedImage = (image: any) => {
        if (!image) return null;
        const galleryId = image.galleryId;
        if (!galleryId) return null;
        const gallery = galleries.find((item) => String(item.id) === String(galleryId));
        return {
            ...image,
            galleryId: String(galleryId),
            galleryTitle: gallery?.title || image.galleryTitle || 'Gallery',
        };
    };

    const getBlockImages = (block: HomeBlock) => {
        return (block.images || [])
            .map((image) => toFeaturedImage(image))
            .filter((image) => image !== null);
    };

    const renderBlockVideo = (block: HomeBlock) => {
        if (!block.videoUrl) return null;
        return (
            <div className="home-block-video">
                <video className="home-block-video__media" src={block.videoUrl} controls preload="metadata" />
            </div>
        );
    };

    const renderBlock = (block: HomeBlock) => {
        if (block.key === 'hero') {
            const blockImages = getBlockImages(block);
            const heroImage = blockImages[0] || allImages.find((image) => image.showOnHomepage) || allImages[0] || null;
            const heroGallery = galleries.find((gallery) => gallery.id === heroImage?.galleryId) || null;

            return (
                <section key={block.id} className="home-hero">
                    {block.videoUrl ? (
                        <div className="home-hero__media">
                            <video src={block.videoUrl} controls preload="metadata" />
                        </div>
                    ) : heroImage ? (
                        <div className="home-hero__media" aria-hidden="true">
                            <img
                                src={getGalleryDisplayImageUrl(heroImage)}
                                srcSet={buildGalleryResponsiveSrcSet(heroImage)}
                                sizes="(max-width: 768px) 100vw, 1180px"
                                alt=""
                                loading="eager"
                                decoding="async"
                            />
                        </div>
                    ) : (
                        <div className="home-hero__media home-hero__media--placeholder" aria-hidden="true" />
                    )}
                    <div className="home-hero__veil" aria-hidden="true" />
                    <div className="home-hero__content">
                        <p className="home-hero__eyebrow">Photocouture</p>
                        <h1>{block.title?.trim() || 'Fine Art Photography with a Couture Eye'}</h1>
                        {block.description ? <p className="home-hero__subline">{block.description}</p> : null}
                        <div className="home-hero__actions">
                            <a className="button button--ghost" href="#home-curated">
                                Explore Gallery
                            </a>
                        </div>
                    </div>
                    {heroGallery && (
                        <Link className="home-hero__series-link" to={buildGalleryPath(heroGallery)}>
                            {heroGallery.title}
                        </Link>
                    )}
                </section>
            );
        }

        if (block.key === 'vision') {
            const blockImages = getBlockImages(block);
            const introImage = blockImages[0] || allImages.find((image) => image.showAsVision) || allImages[0] || null;

            return (
                <section key={block.id} className="home-intro" id="home-intro">
                    {renderBlockVideo(block)}
                    <div className="home-intro__copy">
                        <p className="home-intro__eyebrow">Vision</p>
                        <h2>{block.title?.trim() || 'Photocouture - Art phtography by Finn Albæk'}</h2>
                        {block.description ? (
                            <p>{block.description}</p>
                        ) : (
                            <>
                                <p>
                                    Photocouture combines editorial sensitivity with fine art calm. Each image is
                                    selected to feel tactile, cinematic and quietly expressive.
                                </p>
                                <p>
                                    Explore curated collections built as visual essays, where light and composition lead
                                    the narrative.
                                </p>
                            </>
                        )}
                    </div>
                    <div className="home-intro__media" aria-hidden="true">
                        {introImage ? (
                            <img
                                src={getGalleryDisplayImageUrl(introImage)}
                                srcSet={buildGalleryResponsiveSrcSet(introImage)}
                                sizes="(max-width: 900px) 100vw, 44vw"
                                alt=""
                                loading="lazy"
                                decoding="async"
                            />
                        ) : (
                            <div className="home-intro__placeholder" />
                        )}
                    </div>
                </section>
            );
        }

        if (block.key === 'collection') {
            const blockImages = getBlockImages(block);
            const selectedGalleryIds = Array.from(new Set(blockImages.map((image) => String(image.galleryId))));
            const selectedGalleries = selectedGalleryIds.length
                ? galleries.filter((gallery) => selectedGalleryIds.includes(String(gallery.id)))
                : galleries;

            return (
                <React.Fragment key={block.id}>
                    {renderBlockVideo(block)}
                    <Gallery galleries={selectedGalleries} />
                </React.Fragment>
            );
        }

        if (block.key === 'selection') {
            const blockImages = getBlockImages(block);
            const images = (blockImages.length
                ? blockImages
                : allImages.filter((image) => image.showOnHomepage)).slice(0, 5);

            if (!images.length) return null;

            return (
                <section key={block.id} className="home-signature-strip" aria-label="Signature image strip">
                    {renderBlockVideo(block)}
                    <div className="home-signature-strip__header">
                        <p className="home-showcase__eyebrow">{block.title || 'Selection'}</p>
                    </div>
                    <div className="home-showcase__mosaic">
                        {images.map((image) => (
                            <Link
                                key={`${block.id}-${image.id}-${image.galleryId}`}
                                to={buildGalleryPath({ id: image.galleryId, title: image.galleryTitle })}
                                className="home-showcase__mosaic-item"
                                aria-label={`Åbn galleri ${image.galleryTitle}`}
                            >
                                <img
                                    src={getGalleryDisplayImageUrl(image)}
                                    srcSet={buildGalleryResponsiveSrcSet(image)}
                                    sizes="(max-width: 768px) 44vw, 24vw"
                                    alt={image.title || image.galleryTitle}
                                    loading="lazy"
                                    decoding="async"
                                />
                                <span>{image.title || image.galleryTitle}</span>
                            </Link>
                        ))}
                    </div>
                </section>
            );
        }

        const customImages = getBlockImages(block);
        return (
            <section key={block.id} className="home-custom-block">
                {renderBlockVideo(block)}
                <div className="home-custom-block__copy">
                    <p className="home-showcase__eyebrow">Block</p>
                    <h2>{block.title || block.key}</h2>
                    {block.description ? <p>{block.description}</p> : null}
                </div>
                {!!customImages.length && (
                    <div className="home-custom-block__images">
                        {customImages.map((image) => (
                            <Link
                                key={`${block.id}-${image.id}`}
                                to={buildGalleryPath({ id: image.galleryId, title: image.galleryTitle })}
                                className="home-custom-block__image"
                            >
                                <img
                                    src={getGalleryDisplayImageUrl(image)}
                                    srcSet={buildGalleryResponsiveSrcSet(image)}
                                    sizes="(max-width: 768px) 44vw, 24vw"
                                    alt={image.title || image.galleryTitle}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        );
    };

    return (
        <div className="home">
            {loading ? (
                <div className="gallery-loading">Indlæser kurateringen...</div>
            ) : error ? (
                <div className="gallery-error">{error}</div>
            ) : !galleries.length ? (
                <div className="gallery-empty">Ingen gallerier er publiceret endnu.</div>
            ) : (
                <>
                    {orderedVisibleBlocks.map((block) => renderBlock(block))}
                    <section className="home-closing" id="home-contact">
                        <p className="home-closing__eyebrow">Contact</p>
                        <h2>Available as fine art print. Enquire for size and edition details.</h2>
                        <div className="home-closing__actions">
                            <a className="button button--ghost-dark" href="mailto:contact@photocouture.dk">
                                Contact
                            </a>
                            <Link className="button button--ghost-dark" to="/galleries">
                                View Collections
                            </Link>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};

export default Home;